import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/base/buttons/button'
import { api, ApiError } from '@/shell/api/client'
import { keys } from '@/shell/api/queries'
import { ordinal } from '@/shell/format'
import { useWithStepUp } from '@/shell/hooks/use-step-up'
import { periodLabel } from '@/shell/period'
import { useShell } from '@/shell/store'
import { toast } from './toast'

/**
 * PRD-018 §3.3 / Q3 (decided 2026-09-11; ADR 0032): after the scheduler advances the firm's current open month, every
 * CFO sees one line under the page bar until they answer it. "Keep" records that this person has seen the new month
 * (per person, no step-up: nothing changes). "Hold at ‹prev›" is the real decision: it sets the month back and holds
 * it, through the same Tier 1 `PUT /settings` as Settings → General, and clears the banner for every CFO.
 * Non-CFO roles never see it (the bootstrap omits `autoAdvance` for them).
 */
export function OpenMonthBanner() {
  const { bootstrap } = useShell()
  const qc = useQueryClient()
  const withStepUp = useWithStepUp()
  const advance = bootstrap.period.autoAdvance
  const keep = useMutation({
    mutationFn: () => api('/me/settings', { method: 'PUT', json: { openMonthAck: advance!.to } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.bootstrap }),
    onError: () => toast('Could not save. Try again.', { tone: 'error' }),
  })
  const hold = useMutation({
    mutationFn: () => withStepUp(`Hold the current open month at ${periodLabel(advance!.from)}`, () => api('/settings', { method: 'PUT', json: { currentOpenPeriod: advance!.from, openMonthHold: true } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.bootstrap })
      toast(`Current open month held at ${periodLabel(advance!.from)}. Release it under Settings → General.`, { tone: 'success' })
    },
    onError: (e) => {
      if (e instanceof Error && e.message === 'cancelled')
        return
      toast(e instanceof ApiError ? String(e.body.message ?? 'Could not hold the month.') : 'Could not hold the month. Check your connection and try again.', { tone: 'error' })
    },
  })
  if (!advance)
    return null
  const busy = keep.isPending || hold.isPending
  return (
    <div role="status" className="flex items-center justify-between gap-4 border-b border-secondary bg-brand-secondary px-6 py-1.5 text-sm text-primary" data-testid="open-month-banner">
      <span>
        <span className="font-medium">{`Current open month is now ${periodLabel(advance.to)}.`}</span>
        {' '}
        <span className="text-tertiary">{`Advanced automatically the day after the firm close target day (the ${ordinal(bootstrap.account.closeTargetDay)}).`}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <Button size="sm" color="secondary" onClick={() => hold.mutate()} isDisabled={busy} isLoading={hold.isPending} data-testid="open-month-hold">{`Hold at ${periodLabel(advance.from)}`}</Button>
        <Button size="sm" onClick={() => keep.mutate()} isDisabled={busy} isLoading={keep.isPending} data-testid="open-month-keep">Keep</Button>
      </span>
    </div>
  )
}
